/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { apiConfig } from './apiConfig';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1_susi',
        forgotPassword: 'B2C_1_reset_pwd',
        editProfile: 'B2C_1_profile',
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://Gambooler.b2clogin.com/Gambooler.onmicrosoft.com/B2C_1_susi',
        },
        forgotPassword: {
            authority: 'https://Gambooler.b2clogin.com/Gambooler.onmicrosoft.com/B2C_1_reset_pwd',
        },
        editProfile: {
            authority: 'https://Gambooler.b2clogin.com/Gambooler.onmicrosoft.com/B2C_1_profile',
        },
    },
    authorityDomain: 'Gambooler.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: '881ba93a-8148-4191-bde6-6491d89e33e3', // This is the ONLY mandatory field that you need to supply. // 
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        //redirectUri: 'https://gamboolerdev.z1.web.core.windows.net', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin - default: /
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
/*
export const protectedResources = {
    apiCasino: {
        endpoint: 'https://localhost:7142/Casino/casinos',
        scopes: {
            read: ['https://Gambooler.onmicrosoft.com/45ad20f6-c223-4d8c-a785-249eee0aed3d/Casino.Read'],
            write: ['https://Gambooler.onmicrosoft.com/45ad20f6-c223-4d8c-a785-249eee0aed3d/Casino.Write'],
            manage: ['https://Gambooler.onmicrosoft.com/45ad20f6-c223-4d8c-a785-249eee0aed3d/Casino.Manage']
        },
    }
};
*/

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...apiConfig.casino.scopes.read, ...apiConfig.casino.scopes.write, ...apiConfig.casino.scopes.manage,
    ...apiConfig.people.scopes.read, ...apiConfig.people.scopes.write, ...apiConfig.people.scopes.manage,
    ...apiConfig.user.scopes.read, ...apiConfig.user.scopes.write, ...apiConfig.user.scopes.manage,
    ...apiConfig.signature.scopes.read, ...apiConfig.signature.scopes.write, ...apiConfig.signature.scopes.manage,
    ...apiConfig.club.scopes.read, ...apiConfig.club.scopes.write, ...apiConfig.club.scopes.manage,
    ...apiConfig.book.scopes.read, ...apiConfig.book.scopes.write, ...apiConfig.book.scopes.manage,
    ...apiConfig.ingest.scopes.read, ...apiConfig.ingest.scopes.write, ...apiConfig.ingest.scopes.manage,
    ...apiConfig.notification.scopes.read, ...apiConfig.notification.scopes.write]
};